/**
 * 主题（日/夜）切换工具
 * - 默认深色（dark）；用户选择记忆在 localStorage
 * - 通过 <html data-theme="dark|light"> 生效，配合 src/css/theme.scss 变量
 * - 不涉及任何业务逻辑/接口/权限
 */
const KEY = "app-theme";

export function getTheme() {
  try {
    return localStorage.getItem(KEY) === "light" ? "light" : "dark";
  } catch (e) {
    return "dark";
  }
}

export function applyTheme(mode) {
  const m = mode === "light" ? "light" : "dark";
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("data-theme", m);
  }
  try {
    localStorage.setItem(KEY, m);
  } catch (e) {
    /* ignore */
  }
  // 通知需要按主题重绘的组件（如首页 echarts）
  try {
    window.dispatchEvent(new CustomEvent("themechange", { detail: m }));
  } catch (e) {
    /* ignore */
  }
  return m;
}

export function initTheme() {
  return applyTheme(getTheme());
}

export function toggleTheme() {
  return applyTheme(getTheme() === "dark" ? "light" : "dark");
}

/**
 * 业务图表页的 echarts(v4) 主题对象：`echarts.init(el, chartTheme())` 二参接入。
 * 只提供文字/轴线/分割线/图例/悬浮框的日夜配色兜底，页面 option 里显式写的
 * （系列色、间距、图例数据等）优先级更高、原样生效。
 * 注意：init 的主题参数实例化后不可变——切主题时需 dispose 旧实例后重新调用
 * 各图表渲染方法（页面在 mounted 里监听 themechange 统一处理）。
 * 首页 Main.vue 为定版页，用其自带的内联配色，不走本函数。
 */
export function chartTheme() {
  const dark =
    typeof document !== "undefined" &&
    document.documentElement.getAttribute("data-theme") !== "light";
  const c = dark
    ? {
        title: "#dbe9fb",
        axis: "#8fabce",
        axisLine: "rgba(120,170,230,0.35)",
        split: "rgba(120,170,230,0.12)",
        legend: "#cfe3ff",
        tipBg: "rgba(9,26,50,0.94)",
        tipBorder: "rgba(64,170,255,0.4)",
      }
    : {
        title: "#1a2b45",
        axis: "#5c6b82",
        axisLine: "#d8e2f0",
        split: "#e7edf7",
        legend: "#1f2937",
        tipBg: "rgba(255,255,255,0.96)",
        tipBorder: "#dbe7ff",
      };
  const axisTheme = {
    axisLine: { lineStyle: { color: c.axisLine } },
    axisTick: { lineStyle: { color: c.axisLine } },
    axisLabel: { color: c.axis },
    splitLine: { lineStyle: { color: [c.split] } },
  };
  return {
    textStyle: { color: c.axis },
    title: { textStyle: { color: c.title } },
    legend: { textStyle: { color: c.legend } },
    tooltip: {
      backgroundColor: c.tipBg,
      borderColor: c.tipBorder,
      borderWidth: 1,
      textStyle: { color: c.legend },
    },
    categoryAxis: axisTheme,
    valueAxis: axisTheme,
    timeAxis: axisTheme,
    logAxis: axisTheme,
  };
}
